/**
 * Wire types for the people service's multi-owner endpoints (backend `feature/co-owner`).
 *
 * A vessel has one `owner` — the primary, who holds the plan — and any number of `co-owner`s.
 * The owner moves only through these two calls; `PUT /roles` refuses to touch it.
 */

/** `POST /roles/ownership` — the owner and the named co-owner swap roles and seats. */
export type TransferOwnershipRequestDto = {
  service_id: string;
  person_id: string;
};

/**
 * `POST /roles/ownership/leave` — the owner leaves the vessel and keeps their account.
 *
 * With co-owners, `person_id` (or the longest-serving one) becomes owner. With only captains
 * the vessel goes ownerless and they run it. With neither the backend answers 409
 * `OWNER_MUST_DELETE_VESSEL`. A plan the owner paid for is cancelled at period end.
 */
export type LeaveOwnershipRequestDto = {
  service_id: string;
  person_id?: string;
};

/** One row of `GET /invites/vessel?role=co-owner` (or `role=owner`) — see `getOwnershipInvites`. */
export type CoOwnerInviteDto = {
  id: string;
  person_id?: string;
  service_id: string;
  name: string;
  role: string;
  /** Email or phone; an invite to an existing account shows the email they log in with. */
  contact?: string;
  message?: string;
  created_at: string;
  /** `internal`, `external` or `code` — a one-time invitation code, addressed to nobody. */
  type: string;
};

export type CoOwnerInvite = {
  id: string;
  name: string;
  contact: string;
  message?: string;
  createdAt: string;
};

/**
 * The `type` the backend puts on a refused ownership change. Its readable message only reaches
 * the wire on dev and demo, so the copy has to be ours (see `ownershipErrorMessage`).
 */
export const OWNERSHIP_ERROR = {
  ALREADY_ON_VESSEL: 'ALREADY_ON_VESSEL',
  NOT_ENOUGH_MANAGEMENT_SEATS: 'NOT_ENOUGH_MANAGEMENT_SEATS',
  SEATS_BELOW_OWNER_GROUP: 'SEATS_BELOW_OWNER_GROUP',
  CO_OWNER_REQUIRES_OWNER: 'CO_OWNER_REQUIRES_OWNER',
  INVITER_NOT_ENTITLED: 'INVITER_NOT_ENTITLED',
  ONLY_OWNER_MANAGES_CO_OWNERS: 'ONLY_OWNER_MANAGES_CO_OWNERS',
  ONLY_OWNER_CAN_TRANSFER: 'ONLY_OWNER_CAN_TRANSFER',
  TRANSFER_TARGET_NOT_CO_OWNER: 'TRANSFER_TARGET_NOT_CO_OWNER',
  OWNER_MUST_LEAVE: 'OWNER_MUST_LEAVE',
  OWNER_MUST_DELETE_VESSEL: 'OWNER_MUST_DELETE_VESSEL',
  OWNER_MISMATCH: 'OWNER_MISMATCH',
} as const;
